import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormInput, FormItem, FormLabel } from '@/components/wm';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CHART_TYPES, PALETTE_OPTIONS, recommendedChartType } from '../utils';
import { DEFAULT_LEVEL_GRAPHIC, LEVEL_GRAPHICS, LevelGraphic } from '../Widgets/LevelGraphic';
import { Widget } from './Widget';

// Shared by every widget type: title, subtitle and palette mean the same thing
// everywhere. Fields that only some types render are gated on `data.type`.
export function NewWidgetGeneral ({ data, onChangeField, onCompleted }) {
    const
        paletteOptions = PALETTE_OPTIONS,
        // The legend is the caption under the value card's figure. A chart has no
        // such element, so offering the field would be a control that does nothing.
        hasLegend = data.type === 'value_card',
        hasChartType = data.type === 'indicator_chart',
        hasGraphic = data.type === 'level',
        palette = data.graphical?.palette || 'primary',
        graphic = data.graphical?.graphic || DEFAULT_LEVEL_GRAPHIC;

    const graphicTileVariants = {
        primary: 'tw-bg-primary tw-text-secondary',
        secondary: 'tw-bg-secondary tw-text-gray-200',
        simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
    };

    const
        chartTypeLabels = {
            column: i18next.t('dashboard.custom_dashboard.bar_chart'),
            line: i18next.t('dashboard.custom_dashboard.line_chart')
        },
        recommendedType = hasChartType && recommendedChartType(data.data || {}),
        chartTypeLabel = chartType => (chartType === recommendedType
            ? `${chartTypeLabels[chartType]} (${i18next.t('dashboard.custom_dashboard.recommended')})`
            : chartTypeLabels[chartType]);

    const formSchema = z.object({
        chartType: z.enum(CHART_TYPES).optional(),
        legend: z.string().optional(),
        palette: z.enum(paletteOptions, {
            errorMap: () => ({ message: i18next.t('form_validations.widget.palette_required') })
        }),
        subtitle: z.string().optional(),
        title: z.string().min(1, i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            chartType: data.graphical?.chartType || (hasChartType ? recommendedType : undefined),
            legend: data.legend || '',
            palette: data.graphical?.palette || 'primary',
            subtitle: data.subtitle || '',
            title: data.title || ''
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        palette: debounce(() => form.trigger('palette'), 600),
        title: debounce(() => form.trigger('title'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.title').capitalize()}</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        onChange={e => {
                                            field.onChange(e);
                                            onChangeField('title')(e);
                                        }}
                                        placeholder={i18next.t('glossary.title').capitalize()}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="subtitle"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.subtitle').capitalize()}</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        onChange={e => {
                                            field.onChange(e);
                                            onChangeField('subtitle')(e);
                                        }}
                                        placeholder={i18next.t('glossary.subtitle').capitalize()}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    {hasLegend && (
                        <FormField
                            control={form.control}
                            name="legend"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{i18next.t('common.legend').capitalize()}</FormLabel>
                                    <FormControl>
                                        <FormInput
                                            {...field}
                                            onChange={e => {
                                                field.onChange(e);
                                                onChangeField('legend')(e);
                                            }}
                                            placeholder={i18next.t('common.legend').capitalize()}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}
                    <FormField
                        control={form.control}
                        name="palette"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.palette').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);
                                            // Save to nested graphical.palette structure
                                            onChangeField('graphical')({
                                                ...data.graphical,
                                                palette: val
                                            });
                                            debouncedTriggers.current.palette();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('dashboard.custom_dashboard.select_a_palette')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {paletteOptions.map(palette => (
                                                <SelectItem key={palette} value={palette}>
                                                    {palette.capitalize()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    {hasGraphic && (
                        <FormItem>
                            <FormLabel>{i18next.t('dashboard.custom_dashboard.level_graphic')}</FormLabel>
                            <div
                                aria-label={i18next.t('dashboard.custom_dashboard.level_graphic')}
                                className="tw-grid tw-grid-cols-4 tw-gap-2 tw-p-1"
                                role="radiogroup"
                            >
                                {Object.keys(LEVEL_GRAPHICS).map(key => {
                                    const
                                        selected = graphic === key,
                                        name = i18next.t(`dashboard.custom_dashboard.level_graphics.${key}`);

                                    return (
                                        <button
                                            aria-checked={selected}
                                            aria-label={name}
                                            className={cn(
                                                graphicTileVariants[palette],
                                                'tw-flex tw-h-20 tw-items-center tw-justify-center tw-rounded-sm tw-p-2 tw-transition tw-duration-200',
                                                'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-secondary/50 focus-visible:tw-ring-offset-2',
                                                selected
                                                    ? 'tw-ring-2 tw-ring-secondary tw-ring-offset-2'
                                                    : 'tw-opacity-60 hover:tw-opacity-90'
                                            )}
                                            key={key}
                                            onClick={() => onChangeField('graphical')({ ...data.graphical, graphic: key })}
                                            role="radio"
                                            title={name}
                                            type="button"
                                        >
                                            <LevelGraphic className="tw-h-full tw-w-full" graphic={key} palette={palette} share={0.65}/>
                                        </button>
                                    );
                                })}
                            </div>
                        </FormItem>
                    )}
                    {hasChartType && (
                        <FormField
                            control={form.control}
                            name="chartType"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{i18next.t('glossary.chart_type').capitalize()}</FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={val => {
                                                field.onChange(val);
                                                onChangeField('graphical')({
                                                    ...data.graphical,
                                                    chartType: val
                                                });
                                            }}
                                            value={field.value}
                                        >
                                            <SelectTrigger className="tw-ml-0">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CHART_TYPES.map(chartType => (
                                                    <SelectItem key={chartType} value={chartType}>
                                                        {chartTypeLabel(chartType)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
